import React, { createRef, FC, useMemo, useRef } from 'react';
import { useTranslation, Trans } from '@wix/wix-i18n-config';
import { useInspirationalTemplates } from '../../hooks/useInspirationalTemplates';
import { InspirationalTemplate } from '../InspirationalTemplate/InspirationalTemplate';
import { LinkWithQuery } from '../common/LinkWithQuery/LinkWithQuery';
import {
  INSPIRATIONAL_CATEGORY_SLUG,
  TOP_CONVERTING_CATEGORY_SLUG,
} from '../../config';
import { TopConvertingTemplate } from '../TopConvertingTemplate/TopConvertingTemplate';
import { useInitialState } from '../../contexts/InitialStateContext';

import s from './TemplatesCarousel.scss';
import { useBiLogger } from '../../contexts/BILoggerContext';

const GENERAL_ACTION = 'browse_inspirational_templates';
import { useCarouselScroll } from './hooks/useCarouselScroll';
import { usePageScroll } from '../../contexts/PageScrollContext';
import { useInternalRoutes } from '../../hooks/useInternalRoutes';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { useIsStudio2Ui } from '../../hooks/useIsStudio2Ui';

export const TemplatesCarousel: FC = () => {
  const inspirationalTemplates = useInspirationalTemplates();
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const { scrollToTop } = usePageScroll();
  const { category } = useInternalRoutes();
  const isStudio2Ui = useIsStudio2Ui();
  const { topConvertingTemplates } = useInitialState();
  // the studio2 UI shows curated templates instead of inspirational ones
  const templates = isStudio2Ui
    ? topConvertingTemplates
    : inspirationalTemplates;
  const carouselCategorySlug = isStudio2Ui
    ? TOP_CONVERTING_CATEGORY_SLUG
    : INSPIRATIONAL_CATEGORY_SLUG;

  const handleSeeAllClick = () => {
    scrollToTop();
    biLogger.reportGeneralAction({
      action: GENERAL_ACTION,
      actionValue: 'inspirational_templates',
      categorySlug: INSPIRATIONAL_CATEGORY_SLUG,
    });
  };

  const templatesRefs = useMemo(
    () => templates.map(() => createRef<HTMLDivElement>()),
    [templates],
  );

  const carouselRef = useRef<HTMLDivElement>(null);

  const {
    showNextTemplate,
    showPreviousTemplate,
    isStartPosition,
    isEndPosition,
  } = useCarouselScroll({
    templatesRefs,
    carouselRef,
  });

  const handleCarouselArrowClick =
    (direction: 'backward' | 'forward') => () => {
      biLogger.reportGeneralAction({
        action: GENERAL_ACTION,
        actionValue: direction,
        categorySlug: carouselCategorySlug,
      });
      direction === 'forward' ? showNextTemplate() : showPreviousTemplate();
    };

  return (
    <div className={s.container}>
      {isStudio2Ui ? (
        <div className={s.textBlock}>
          <div className={s.title}>
            {t('app.templates.carousel.studio2.title')}
          </div>
          <div className={s.description}>
            {t('app.templates.carousel.studio2.description')}
          </div>
        </div>
      ) : (
        <div className={s.textBlock}>
          <div className={s.title}>{t('app.templates.carousel.title')}</div>
          <div className={s.description}>
            <Trans
              i18nKey="app.templates.carousel.description"
              components={{
                Link: (
                  <LinkWithQuery
                    to={category.build(INSPIRATIONAL_CATEGORY_SLUG)}
                    className={s.seeAllLink}
                    onClick={handleSeeAllClick}
                  />
                ),
              }}
            />
          </div>
        </div>
      )}
      <div className={s.carousel} ref={carouselRef}>
        {isStudio2Ui
          ? topConvertingTemplates.map((template, index) => (
              <TopConvertingTemplate
                className={s.template}
                ref={templatesRefs[index]}
                key={template.templateId}
                template={template}
                index={index}
              />
            ))
          : inspirationalTemplates.map((template, index) => (
              <InspirationalTemplate
                className={s.template}
                ref={templatesRefs[index]}
                key={template.templateId}
                template={template}
                backgroundColor={template.backgroundColor}
                index={index}
                withTag
              />
            ))}
      </div>
      <div className={s.arrowsContainer}>
        <button
          className={s.arrow}
          disabled={isStartPosition}
          onClick={handleCarouselArrowClick('backward')}
        >
          {isStudio2Ui ? <ChevronLeft /> : '←'}
        </button>
        <button
          className={s.arrow}
          disabled={isEndPosition}
          onClick={handleCarouselArrowClick('forward')}
        >
          {isStudio2Ui ? <ChevronRight /> : '→'}
        </button>
      </div>
    </div>
  );
};
